import { useCallback, useRef } from 'react';

/**
 * Wraps an async action so calls made while an earlier one is still running
 * are ignored (they resolve to `undefined`). For buttons whose action awaits
 * something before it disables itself, so a double-click cannot run it twice.
 */
const useSingleFlight = <A extends unknown[], R>(
  fn: (...args: A) => Promise<R>,
) => {
  const running = useRef(false);
  const latest = useRef(fn);
  latest.current = fn;

  return useCallback(async (...args: A): Promise<R | undefined> => {
    if (running.current) return undefined;
    running.current = true;
    try {
      return await latest.current(...args);
    } finally {
      running.current = false;
    }
  }, []);
};

export default useSingleFlight;
